// SILL-173: widget for the journey-rail "enter-data" step's detail
// pane. Shows the percent, an enumerated "Still to add" list when any
// onboarding fields are unfilled, and a CTA back to onboarding. The
// CTA hides entirely once the profile is complete (no missing fields).
// Pure UI; the controller decides where "continue" lands.

import { ArrowRight } from "lucide-react";

import { Button } from "../../ui/button";

export interface ProfileCompletenessWidgetProps {
  onContinue: () => void;
  percent: number;
  // User-facing labels for the fields the user hasn't filled yet. An
  // empty list means the profile is complete — the CTA is hidden and
  // a short "Profile complete" affordance renders instead.
  missing?: readonly string[];
}

export function ProfileCompletenessWidget({
  onContinue,
  percent,
  missing = [],
}: ProfileCompletenessWidgetProps) {
  const isComplete = missing.length === 0;
  // `percent` now comes from the live, re-based completeness (readiness
  // engine, projected onto /api/users/me) and `missing` is derived from the
  // same five financial rows, so they agree by construction. This clamp +
  // empty-list→100 stays as a defensive backstop for transient skew (e.g.
  // the rare no-active-target user whose percent is still the frozen value).
  const safe = isComplete
    ? 100
    : Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      className="flex w-full flex-col gap-3"
      data-slot="profile-completeness-widget"
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-foreground">
          Profile completeness
        </p>
        <span className="text-2xl font-semibold tracking-[-0.02em] text-foreground">
          {safe}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-border">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${safe}%` }}
        />
      </div>
      {isComplete ? (
        <p
          className="text-xs font-medium text-primary"
          data-slot="profile-completeness-complete"
        >
          Profile complete — nothing left to add.
        </p>
      ) : (
        <>
          <div
            className="flex flex-col gap-1"
            data-slot="profile-completeness-missing"
          >
            <p className="text-xs font-medium text-muted-foreground">
              Still to add
            </p>
            <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-foreground">
              {missing.map((label) => (
                <li key={label}>· {label}</li>
              ))}
            </ul>
          </div>
          <Button
            className="self-start"
            onClick={onContinue}
            size="sm"
            variant="outline"
          >
            Continue onboarding
            <ArrowRight aria-hidden="true" className="ml-1 h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}
